/* eslint-disable react-hooks/purity -- confetti is random by design; a re-render reshuffling pieces is harmless */
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

/** Motion of one piece; internal to NavioConfetti, which stories cover. */
interface ConfettiPieceMotion {
  originX: number;
  originY: number;
  angle: number;
  distance: number;
  color: string;
  delay: number;
  duration: number;
}

const ConfettiPiece = ({ originX, originY, angle, distance, color, delay, duration }: ConfettiPieceMotion) => {
  const rotation = Math.random() * 360;
  const shape = Math.random() > 0.5 ? 'square' : 'circle';
  const size = Math.random() * 8 + 4;

  // Calculate end position using angle and distance (radial spread)
  const radians = (angle * Math.PI) / 180;
  const endX = Math.cos(radians) * distance;
  const endY = Math.sin(radians) * distance;

  // Add some randomness to make it more natural
  const randomVariation = (Math.random() - 0.5) * 100;
  const finalX = endX + randomVariation;
  const finalY = endY + randomVariation;

  const rotationSpeed = (Math.random() - 0.5) * 1080;

  const variants = {
    initial: {
      x: 0,
      y: 0,
      rotate: rotation,
      opacity: 1,
      scale: 1
    },
    animate: {
      x: finalX,
      y: finalY,
      rotate: rotation + rotationSpeed,
      opacity: [1, 1, 0.8, 0],
      scale: [1, 1.2, 0.8, 0]
    }
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: `${originX}px`,
        top: `${originY}px`,
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
        borderRadius: shape === 'circle' ? '50%' : '0%',
        transformOrigin: 'center center'
      }}
      variants={variants}
      initial='initial'
      animate='animate'
      transition={{
        duration,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] // Ease-out for burst effect
      }}
    />
  );
};

type Props = {
  /** Reference to the element to emit confetti from */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Number of confetti pieces to render. Default: 50 */
  pieceCount?: number;
  /** Colors array for confetti pieces. Default: colorful palette */
  colors?: string[];
  /** Duration of the animation in seconds. Default: 3 */
  duration?: number;
  /** Whether to trigger the animation automatically on mount. Default: true */
  autoStart?: boolean;
  /** Callback when animation completes */
  onComplete?: () => void;
};

const DEFAULT_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Sky Blue
  '#F8B739', // Orange
  '#EC7063' // Coral
];

/** Celebratory burst from `triggerRef` -- the success moment of a confirmed order or finished flow. */
export const NavioConfetti = ({
  triggerRef,
  pieceCount = 50,
  colors = DEFAULT_COLORS,
  duration = 3,
  autoStart = true,
  onComplete
}: Props) => {
  const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null);
  const originCapturedRef = useRef(false);
  // CSS reduced-motion rules do not reach Framer Motion transforms.
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!autoStart || !triggerRef?.current || originCapturedRef.current) {
      return;
    }

    // Get the position of the trigger element
    const updateOrigin = () => {
      if (triggerRef?.current && !originCapturedRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setOrigin({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        });
        // Mark origin as captured to prevent further updates
        originCapturedRef.current = true;
      }
    };

    // Wait a bit for React layout to settle
    const timeout = setTimeout(updateOrigin, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [autoStart, triggerRef]);

  useEffect(() => {
    if (onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, duration * 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [duration, onComplete]);

  if (!origin || reduceMotion) {
    return null;
  }

  // Create confetti pieces that radiate outward in all directions
  const pieces = Array.from({ length: pieceCount }, (_, i) => {
    // Distribute angles evenly around 360 degrees
    const angle = (360 / pieceCount) * i + Math.random() * 10 - 5;
    const baseDistance = 200 + Math.random() * 150;

    return {
      id: i,
      angle,
      distance: baseDistance,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 0.3,
      duration: duration + (Math.random() - 0.5) * 0.6
    };
  });

  return (
    <div
      className='pointer-events-none fixed inset-0 z-overlay overflow-hidden'
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {pieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          originX={origin.x}
          originY={origin.y}
          angle={piece.angle}
          distance={piece.distance}
          color={piece.color ?? '#6BCB77'}
          delay={piece.delay}
          duration={piece.duration}
        />
      ))}
    </div>
  );
};
